import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { notify } from '@/lib/notify';

const STOCK_TAKE_DELETE_ROLES = new Set(['tenantAdmin', 'admin', 'platformAdmin']);

export function canDeleteStockTake(roles: string[]): boolean {
  return roles.some((role) => STOCK_TAKE_DELETE_ROLES.has(role));
}

function confirmCopy(status: string): { title: string; description: string } {
  if (status === 'COMPLETED') {
    return {
      title: 'Delete stock take?',
      description:
        'Permanently deletes this stock take. If nothing moved after the count, approval adjustments are reversed. If later warehouse activity exists, current stock is left as-is.',
    };
  }
  return {
    title: 'Delete stock take?',
    description:
      'This permanently removes the stock take. Stock levels will not change.',
  };
}

interface DeleteStockTakeButtonProps {
  stockTakeId: string;
  status: string;
  onDeleted: () => void;
  /** Icon-only trigger for dense toolbars / row actions. */
  iconOnly?: boolean;
}

export function DeleteStockTakeButton({
  stockTakeId,
  status,
  onDeleted,
  iconOnly = false,
}: DeleteStockTakeButtonProps) {
  const { roles } = useAuth();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!canDeleteStockTake(roles)) {
    return null;
  }

  const copy = confirmCopy(status);

  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/v1/stock-takes/${stockTakeId}`, { method: 'DELETE' });
      if (res.status === 204 || res.ok) {
        notify.success('Stock take deleted');
        setOpen(false);
        onDeleted();
        return;
      }
      let message = 'Failed to delete stock take';
      try {
        message = formatApiErrorBody(await res.json());
      } catch {
        // keep default
      }
      notify.error(message);
    } catch {
      notify.error('Failed to delete stock take');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant={iconOnly ? 'ghost' : 'destructive'}
        size={iconOnly ? 'sm' : 'default'}
        aria-label="Delete stock take"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Trash2 className={iconOnly ? 'h-4 w-4' : 'mr-2 h-4 w-4'} />
        {!iconOnly && 'Delete stock take'}
      </Button>

      <Dialog open={open} onOpenChange={(next) => !deleting && setOpen(next)}>
        <DialogContent
          onClick={(e) => e.stopPropagation()}
          onPointerDownOutside={(e) => {
            if (deleting) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={deleting}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleting}
              onClick={() => void handleDelete()}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
